// Small UI primitives for the home screen (identical in Docs, Sheets and
// Slides). Plain Tailwind + Fowlguard icons so Slides — which has no shadcn
// components — can use the exact same file.

import {
    Fragment, useCallback, useEffect, useId, useLayoutEffect, useRef, useState,
    type ComponentType, type FormEvent, type KeyboardEvent, type ReactNode
} from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "@fg/icons";

export function cx(...parts: Array<string | false | null | undefined>): string {
    return parts.filter(Boolean).join(" ");
}

const FOCUS_RING = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background";

export const BTN_PRIMARY = cx(
    "inline-flex h-9 items-center justify-center gap-1.5 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50",
    FOCUS_RING
);
export const BTN_DANGER = cx(
    "inline-flex h-9 items-center justify-center gap-1.5 rounded-md bg-destructive px-4 text-sm font-medium text-destructive-foreground shadow-sm hover:bg-destructive/90 disabled:pointer-events-none disabled:opacity-50",
    FOCUS_RING
);
export const BTN_OUTLINE = cx(
    "inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-input bg-background px-3 text-sm font-medium shadow-sm hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50",
    FOCUS_RING
);
export const BTN_GHOST = cx(
    "inline-flex h-9 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50",
    FOCUS_RING
);
export const BTN_ICON = cx(
    "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:bg-accent hover:text-foreground",
    FOCUS_RING
);
export const SELECT = cx(
    "h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground",
    FOCUS_RING
);

// ── Menu ─────────────────────────────────────────────────────────────────

export interface MenuAction {
    id: string;
    label: string;
    icon: ComponentType<{ className?: string }>;
    onSelect: () => void;
    danger?: boolean;
    separatorBefore?: boolean;
}

const MENU_WIDTH = 208; // w-52

/**
 * Button + popup menu. The popup is portalled to <body> with fixed
 * positioning so it's never clipped by a card or a scrolling list, and
 * flips above the trigger near the bottom of the viewport.
 */
export function ActionMenu({
    label, actions, children, triggerClassName, align = "end", tabIndex
}: {
    label: string;
    actions: MenuAction[];
    children: ReactNode;
    triggerClassName?: string;
    align?: "start" | "end";
    tabIndex?: number;
}) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const menuId = useId();

    const close = useCallback((refocus: boolean) => {
        setOpen(false);
        setPos(null);
        if (refocus) triggerRef.current?.focus();
    }, []);

    useLayoutEffect(() => {
        if (!open) return;
        const trigger = triggerRef.current;
        const menu = menuRef.current;
        if (!trigger || !menu) return;
        const rect = trigger.getBoundingClientRect();
        const height = menu.offsetHeight;
        let top = rect.bottom + 4;
        if (top + height > window.innerHeight - 8) top = Math.max(8, rect.top - height - 4);
        const preferred = align === "end" ? rect.right - MENU_WIDTH : rect.left;
        const left = Math.min(Math.max(8, preferred), window.innerWidth - MENU_WIDTH - 8);
        setPos({ top, left });
        menu.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }, [open, align]);

    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
            close(false);
        };
        const onScroll = (e: Event) => {
            if (menuRef.current?.contains(e.target as Node)) return;
            close(false);
        };
        const onResize = () => close(false);
        document.addEventListener("mousedown", onPointerDown);
        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("resize", onResize);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("resize", onResize);
        };
    }, [open, close]);

    const onMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
        if (items.length === 0) return;
        const index = items.indexOf(document.activeElement as HTMLElement);
        const focusAt = (i: number) => items[(i + items.length) % items.length]?.focus();
        if (e.key === "ArrowDown") { e.preventDefault(); focusAt(index + 1); }
        else if (e.key === "ArrowUp") { e.preventDefault(); focusAt(index - 1); }
        else if (e.key === "Home") { e.preventDefault(); focusAt(0); }
        else if (e.key === "End") { e.preventDefault(); focusAt(items.length - 1); }
        else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(true); }
        else if (e.key === "Tab") close(false);
    };

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                aria-label={typeof children === "string" ? undefined : label}
                title={label}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                tabIndex={tabIndex}
                onClick={(e) => {
                    e.stopPropagation();
                    if (open) close(false);
                    else setOpen(true);
                }}
                onKeyDown={(e) => {
                    if (e.key === "ArrowDown" && !open) {
                        e.preventDefault();
                        setOpen(true);
                    }
                }}
                className={triggerClassName}
            >
                {children}
            </button>
            {open
                ? createPortal(
                    <div
                        ref={menuRef}
                        id={menuId}
                        role="menu"
                        aria-label={label}
                        onKeyDown={onMenuKeyDown}
                        className="fixed z-[60] w-52 rounded-md border bg-popover p-1 text-popover-foreground shadow-lg"
                        style={pos ?? { top: -9999, left: -9999 }}
                    >
                        {actions.map((action) => {
                            const Icon = action.icon;
                            return (
                                <Fragment key={action.id}>
                                    {action.separatorBefore ? <div role="separator" className="my-1 h-px bg-border" /> : null}
                                    <button
                                        type="button"
                                        role="menuitem"
                                        tabIndex={-1}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            close(true);
                                            action.onSelect();
                                        }}
                                        className={cx(
                                            "flex w-full items-center gap-2.5 rounded-sm px-2.5 py-1.5 text-left text-sm outline-none",
                                            "hover:bg-accent focus:bg-accent",
                                            action.danger ? "text-destructive" : "text-popover-foreground"
                                        )}
                                    >
                                        <Icon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
                                        <span className="truncate">{action.label}</span>
                                    </button>
                                </Fragment>
                            );
                        })}
                    </div>,
                    document.body
                )
                : null}
        </>
    );
}

// ── Dialogs ──────────────────────────────────────────────────────────────

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
    title, onClose, children, footer
}: {
    title: string;
    onClose: () => void;
    children: ReactNode;
    footer: ReactNode;
}) {
    const titleId = useId();
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const previous = document.activeElement as HTMLElement | null;
        const panel = panelRef.current;
        (panel?.querySelector<HTMLElement>("[data-autofocus]") ?? panel?.querySelector<HTMLElement>(FOCUSABLE))?.focus();
        return () => previous?.focus?.();
    }, []);

    const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
            return;
        }
        if (e.key !== "Tab") return;
        const focusables = Array.from(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onKeyDown={onKeyDown}
                className="flex w-full max-w-md flex-col overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-xl"
            >
                <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
                    <h2 id={titleId} className="truncate text-sm font-semibold">{title}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className={cx("flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground", FOCUS_RING)}
                    >
                        <X className="h-4 w-4" aria-hidden />
                    </button>
                </div>
                {children}
                <div className="flex items-center justify-end gap-2 border-t bg-muted/30 px-4 py-3">{footer}</div>
            </div>
        </div>,
        document.body
    );
}

export function RenameDialog({
    initialName, onCancel, onSubmit
}: {
    initialName: string;
    onCancel: () => void;
    /** Rejects with a user-facing message (e.g. Drive's duplicate-name error). */
    onSubmit: (name: string) => Promise<void>;
}) {
    const [name, setName] = useState(initialName);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const formId = useId();

    useEffect(() => {
        // Select the base name only, as file managers do.
        const input = inputRef.current;
        if (!input) return;
        const dot = input.value.lastIndexOf(".");
        input.setSelectionRange(0, dot > 0 ? dot : input.value.length);
    }, []);

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        if (busy) return;
        if (name.trim() === initialName) { onCancel(); return; }
        setBusy(true);
        setError(null);
        try {
            await onSubmit(name);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
            setBusy(false);
        }
    };

    return (
        <Modal
            title="Rename"
            onClose={onCancel}
            footer={
                <>
                    <button type="button" onClick={onCancel} className={BTN_GHOST}>Cancel</button>
                    <button type="submit" form={formId} disabled={busy || !name.trim()} className={BTN_PRIMARY}>
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                        Rename
                    </button>
                </>
            }
        >
            <form id={formId} onSubmit={(e) => void submit(e)} className="flex flex-col gap-2 p-4">
                <label htmlFor={`${formId}-name`} className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Name
                </label>
                <input
                    id={`${formId}-name`}
                    ref={inputRef}
                    data-autofocus
                    value={name}
                    disabled={busy}
                    onChange={(e) => setName(e.target.value)}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? `${formId}-error` : undefined}
                    className={cx("h-9 w-full rounded-md border border-input bg-background px-3 text-sm", FOCUS_RING)}
                />
                {error ? <p id={`${formId}-error`} role="alert" className="text-sm text-destructive">{error}</p> : null}
            </form>
        </Modal>
    );
}

export function ConfirmDialog({
    title, message, confirmLabel, onCancel, onConfirm
}: {
    title: string;
    message: ReactNode;
    confirmLabel: string;
    onCancel: () => void;
    onConfirm: () => Promise<void>;
}) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const confirm = async () => {
        setBusy(true);
        setError(null);
        try {
            await onConfirm();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
            setBusy(false);
        }
    };
    return (
        <Modal
            title={title}
            onClose={onCancel}
            footer={
                <>
                    <button type="button" onClick={onCancel} className={BTN_GHOST} data-autofocus>Cancel</button>
                    <button type="button" onClick={() => void confirm()} disabled={busy} className={BTN_DANGER}>
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                        {confirmLabel}
                    </button>
                </>
            }
        >
            <div className="flex flex-col gap-2 p-4 text-sm">
                <p>{message}</p>
                {error ? <p role="alert" className="text-destructive">{error}</p> : null}
            </div>
        </Modal>
    );
}

// ── Toast ────────────────────────────────────────────────────────────────

export interface ToastState {
    id: number;
    message: string;
    tone: "info" | "error";
    action?: { label: string; run: () => void };
}

export function Toast({ toast, onDismiss }: { toast: ToastState | null; onDismiss: () => void }) {
    useEffect(() => {
        if (!toast) return;
        const timer = window.setTimeout(onDismiss, toast.tone === "error" ? 8000 : 5000);
        return () => window.clearTimeout(timer);
    }, [toast, onDismiss]);

    return (
        <div aria-live="polite" role="status" className="pointer-events-none fixed inset-x-0 bottom-6 z-[80] flex justify-center px-4">
            {toast ? (
                <div
                    className={cx(
                        "pointer-events-auto flex max-w-md items-center gap-3 rounded-md px-4 py-2.5 text-sm shadow-lg",
                        toast.tone === "error" ? "bg-destructive text-destructive-foreground" : "bg-foreground text-background"
                    )}
                >
                    <span className="min-w-0 flex-1">{toast.message}</span>
                    {toast.action ? (
                        <button
                            type="button"
                            onClick={() => { toast.action?.run(); onDismiss(); }}
                            className="shrink-0 rounded px-1.5 py-0.5 font-semibold underline-offset-2 hover:underline"
                        >
                            {toast.action.label}
                        </button>
                    ) : null}
                    <button type="button" onClick={onDismiss} aria-label="Dismiss" className="shrink-0 rounded p-0.5 opacity-80 hover:opacity-100">
                        <X className="h-4 w-4" aria-hidden />
                    </button>
                </div>
            ) : null}
        </div>
    );
}
